/**
 * Client for Phase 3 slice 6a/6b — broadcaster-owned RTP ingest into the SFU.
 * Mutating routes require the same HMAC grant as live tools.
 */

const DEFAULT_BACKEND = (
  (typeof process !== 'undefined' && (process as any).env?.REACT_APP_BACKEND_URL) || ''
) as string;

function backendUrl(override?: string): string {
  return override || DEFAULT_BACKEND || (typeof window !== 'undefined' ? window.location.origin : '');
}

export interface SfuIngestRtpInfo {
  ip: string;
  port: number;
  rtcpPort: number | null;
  payloadType: number;
  ssrc: number;
  mimeType: string;
  clockRate: number;
}

export interface SfuIngestSession {
  ingestId: string;
  sourceId: string;
  kind: string;
  producerId: string;
  rtp: SfuIngestRtpInfo;
  srtpParameters?: unknown;
  createdAt?: number;
  lastActivityAt?: number;
}

async function apiFetch<T>(
  path: string,
  init: RequestInit,
  opts: { backend?: string; sessionId: string; grantToken: string },
): Promise<T> {
  const url = `${backendUrl(opts.backend)}${path}`;
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'X-Live-Session-Id': opts.sessionId,
    'X-Live-Session-Grant': opts.grantToken,
    ...((init.headers || {}) as Record<string, string>),
  };
  const resp = await fetch(url, { ...init, headers });
  const text = await resp.text();
  let json: any = null;
  try { json = text ? JSON.parse(text) : {}; } catch { json = { raw: text }; }
  if (!resp.ok) {
    const err = new Error(json?.error || json?.message || `HTTP ${resp.status}`);
    (err as any).status = resp.status;
    (err as any).body = json;
    throw err;
  }
  return json as T;
}

export async function getSfuIngestCapabilities(opts: { backend?: string } = {}): Promise<any> {
  const url = `${backendUrl(opts.backend)}/api/sfu/ingest/capabilities`;
  const resp = await fetch(url);
  const json = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(json?.error || `HTTP ${resp.status}`);
  return json;
}

export async function startSfuIngest(
  body: {
    sourceId: string;
    kind?: string;
    codec?: string;
    payloadType?: number;
    ssrc?: number;
    enableSrtp?: boolean;
  },
  opts: { backend?: string; sessionId: string; grantToken: string },
): Promise<SfuIngestSession> {
  const out = await apiFetch<{ ok: boolean; ingest: SfuIngestSession }>(
    '/api/sfu/ingest',
    {
      method: 'POST',
      body: JSON.stringify({
        sessionId: opts.sessionId,
        grantToken: opts.grantToken,
        ...body,
      }),
    },
    opts,
  );
  return out.ingest;
}

export async function listSfuIngest(
  opts: { backend?: string; sessionId: string; grantToken: string },
): Promise<SfuIngestSession[]> {
  const q = new URLSearchParams({ sessionId: opts.sessionId, grantToken: opts.grantToken });
  const out = await apiFetch<{ ok: boolean; ingest: SfuIngestSession[] }>(
    `/api/sfu/ingest?${q.toString()}`,
    { method: 'GET' },
    opts,
  );
  return out.ingest || [];
}

export async function stopSfuIngest(
  ingestId: string,
  opts: { backend?: string; sessionId: string; grantToken: string },
): Promise<void> {
  const q = new URLSearchParams({ sessionId: opts.sessionId, grantToken: opts.grantToken });
  await apiFetch<{ ok: boolean }>(
    `/api/sfu/ingest/${encodeURIComponent(ingestId)}?${q.toString()}`,
    { method: 'DELETE' },
    opts,
  );
}
